import { useMemo } from "react";
import type { DimRow, MetricsResponse, RangeParams, StatsResponse } from "../lib/api";
import { buildSeries } from "../lib/format";
import { BarList, type BarListItem } from "./BarList";
import { SeriesChart } from "./SeriesChart";
import { StatCards } from "./StatCards";

export function OverviewContent({
  stats,
  metrics,
  range,
  tz,
}: {
  stats: StatsResponse;
  metrics: MetricsResponse;
  range: RangeParams;
  tz: string | undefined;
}) {
  const series = useMemo(() => buildSeries(range, stats.series), [range, stats.series]);

  function items(dim: string): BarListItem[] {
    return (metrics.tables[dim] ?? []).map((row: DimRow) => ({
      key: `${dim}:${row.val}`,
      label: row.val === "" ? "Langsung" : row.val,
      value: row.visitors,
      extra: row.pageviews,
    }));
  }

  const utmSections: Array<{ title: string; dim: string }> = [
    { title: "Sumber UTM", dim: "utm_source" },
    { title: "Medium UTM", dim: "utm_medium" },
    { title: "Kampanye UTM", dim: "utm_campaign" },
  ];

  return (
    <div className="space-y-4">
      <StatCards totals={stats.totals} />

      <section className="card p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Tren</h3>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-emerald-500" /> Pengunjung
            </span>
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-blue-500" /> Pageview
            </span>
          </div>
        </div>
        <SeriesChart data={series} unit={range.unit} tz={tz} />
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <BarList title="Halaman" items={items("path")} extraLabel="Pageview" />
        <BarList title="Halaman Masuk" items={items("path_entry")} extraLabel="Pageview" />
        <BarList title="Sumber" items={items("ref_domain")} extraLabel="Pageview" />
        <BarList title="Browser" items={items("browser")} extraLabel="Pageview" />
        <BarList title="Sistem Operasi" items={items("os")} extraLabel="Pageview" />
        <BarList title="Perangkat" items={items("device")} extraLabel="Pageview" />
        <BarList title="Negara" items={items("country")} extraLabel="Pageview" />
        <BarList title="Kota" items={items("city")} extraLabel="Pageview" />
        <BarList title="Bahasa" items={items("language")} extraLabel="Pageview" />
        <BarList title="Peristiwa Kustom" items={items("event")} valueLabel="Jumlah" extraLabel="Pengunjung" />
        {utmSections.map((section) =>
          (metrics.tables[section.dim] ?? []).length > 0 ? (
            <BarList key={section.dim} title={section.title} items={items(section.dim)} extraLabel="Pageview" />
          ) : null,
        )}
      </div>
    </div>
  );
}
